import { Button, Icon, Link, Stack, Text, Tooltip, toast } from "@chakra-ui/react"
import { createColumnHelper } from "@tanstack/react-table"
import { ConfirmationDialog } from "components/confirmation/confirmation"
import { ActionDrawer } from "components/drawer"
import { defaultColumns } from "components/table/defaultColumns"
import { removeContent } from "lib/firebase"
import { contentTime, formatContentTime } from "lib/content"
import { formatBytes } from "lib/utils"
import { formatSeconds } from "lib/utils"
import { resolutionKey } from "lib/resolution"
import { UsedDot } from "components/selection/common"
import ContentBroadcasts from "views/modal/content-broadcasts"
import VideoPlayer from "../components/video-player"
import Thumbnail from "../components/thumbnail"
import moment from "moment"
import ContentModal from "views/modal/content"
const helper = createColumnHelper()

export const columnsContent = [
  helper.display({
    id: 'thumbnail',
    header: '',
    size: 20,
    cell: props => (
      <Thumbnail content={props.row.original} />
    ),
  }),
  helper.accessor((row) => row.displayName || row.filename, {
    id: 'name',  // Required for computed columns (no single accessorKey)
    header: 'Name',
    size: 700,
    cell: props => {
      // Green dot: the content is played in at least one broadcast
      const used = props.table.options.context?.usage?.get(props.row.original.id) || []
      return (
        <Stack direction='row' spacing={2} align='center'>
          <UsedDot count={used.length} names={used.map(b => b.name)} />
          <Text fontWeight='semibold'>
            {props.getValue()}
          </Text>
        </Stack>
      )
    },
  }),
  helper.accessor('duration', {
    size: 100,
    meta: { searchText: item => item.duration ? formatSeconds(item.duration) : 'N/A' },
    cell: props => (
      <Text>
        {props.getValue() ? formatSeconds(props.getValue()) : 'N/A'}
      </Text>
    ),
  }),
  // "1280x720"; blank for content that has no width / height stored (older uploads), which then
  // sorts last
  helper.accessor((row) => resolutionKey(row) ?? undefined, {
    id: 'resolution',
    size: 110,
    header: 'Resolution',
    meta: { searchText: item => resolutionKey(item) || 'N/A' },
    cell: props => (
      <Text>
        {props.getValue() || 'N/A'}
      </Text>
    ),
  }),
  helper.accessor('size', {
    size: 100,
    meta: { searchText: item => item.size ? formatBytes(item.size) : 'N/A' },
    cell: props => (
      <Text>
        {props.getValue() ? formatBytes(props.getValue()): 'N/A'}
      </Text>
    ),
  }),
  // Sorted by the real time in milliseconds, so a record without a stored time falls back to
  // the creation time in its id and still lands in the right place
  helper.accessor((row) => contentTime(row) ?? undefined, {
    id: 'timestamp',
    size: 200,
    header: 'Uploaded',
    meta: { searchText: item => formatContentTime(item) || 'Unknown' },
    cell: props => (
      <Text>
        {props.getValue() ? moment(props.getValue()).format('LLL'): 'Unknown'}
      </Text>
    ),
  }),
  // The regular broadcasts that play this content; a click opens the list to add / remove it.
  // Not sortable (the number lives in the page context, not in the row).
  helper.accessor((row) => row.id, {
    id: 'broadcasts',
    size: 150,
    header: 'Broadcasts',
    enableSorting: false,
    meta: {
      searchText: (item, context) => {
        const count = context?.usage?.get(item.id)?.length || 0
        return count === 0 ? 'Not used' : (count === 1 ? '1 broadcast' : `${count} broadcasts`)
      },
    },
    cell: props => {
      const item = props.row.original
      const used = props.table.options.context?.usage?.get(item.id) || []
      return (
        <ActionDrawer
          title={'Broadcasts of: ' + (item.displayName || item.filename)}
          size='xl'
          trigger={
            <Tooltip label={used.length > 0 ? used.map(b => b.name).join(', ') : 'Not in any broadcast. Click to add it.'}>
              <Button variant='ghost' size='xs' colorScheme={used.length > 0 ? 'green' : 'gray'}>
                {used.length === 0 ? 'Not used' : (used.length === 1 ? '1 broadcast' : `${used.length} broadcasts`)}
              </Button>
            </Tooltip>
          }
          >
          <ContentBroadcasts content={item} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
      )
    },
  }),
  helper.display({
    id: 'actions',
    header: 'Actions',
    size: 150,
    cell: props => (
      <Stack direction='row' spacing={2} align='center'>
        <VideoPlayer 
          content={props.row.original}
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              View
            </Button>
          }
        />
        <ActionDrawer
          title='Update content'
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Update
            </Button>
          }
          >
          <ContentModal content={props.row.original} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
        <ConfirmationDialog
          title='Delete content'
          message='Are you sure you want to delete this content?'
          confirmLabel='Delete'
          trigger={
            <Button colorScheme='red' variant='ghost' size='xs'>
              Delete
            </Button>
          }
          onConfirm={async () => {
            await removeContent(props.row.original)
            props.table.options.context?.refresh()
          }}
          />
      </Stack>
    ),
  }),
]